import React, { useState } from "react";
import { Facebook, MapPin, Copy, Check } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import EditableText from "./EditableText";
import { contactInfo } from "../mock";

const ContactSection = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [addressCopied, setAddressCopied] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(contactInfo.address);
      setAddressCopied(true);
      toast({ title: "Address copied", description: "Paste it into your favourite map app." });
      setTimeout(() => setAddressCopied(false), 2200);
    } catch (e) {
      toast({ title: "Copy failed", description: "Please select and copy the address text manually." });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast({ title: "Please fill required fields", description: "Name, Contact Number and Message are required." });
      return;
    }
    setSubmitting(true);
    // Persist locally as a backup
    try {
      const list = JSON.parse(localStorage.getItem("srt_enquiries") || "[]");
      list.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem("srt_enquiries", JSON.stringify(list));
    } catch (err) { /* ignore */ }

    // Send email directly via FormSubmit (free, no signup, no backend needed).
    // The trust's Gmail will receive the enquiry as an email. FormSubmit
    // requires a one-time activation link on the FIRST submission after
    // deployment — the trust just needs to click "Activate" in the first
    // confirmation email they get.
    try {
      const to = contactInfo.email; // sharadaramaraotrust2014@gmail.com
      const resp = await fetch(`https://formsubmit.co/ajax/${encodeURIComponent(to)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email || "(not provided)",
          phone: form.phone,
          message: form.message,
          _subject: `Enquiry from ${form.name} \u2014 Sharada Rama Rao Trust`,
          _template: "table",
          _captcha: "false",
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && (data.success === "true" || data.success === true || data.success === undefined)) {
        toast({
          title: "Enquiry sent",
          description: "Thank you! The Trust has been notified and will get back to you soon.",
        });
        setForm({ name: "", email: "", phone: "", message: "" });
      } else {
        throw new Error(data.message || "Submission failed");
      }
    } catch (err) {
      toast({
        title: "Could not send",
        description: "Please try again in a moment or reach us on WhatsApp / Facebook.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="w-full bg-[#d6d7c5] py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-10">
        <div>
          <div className="flex items-start gap-3 mb-6">
            <EditableText id="contact.title" as="h2"
                className="text-[#1a1a1a] font-semibold block"
                style={{ fontFamily: "'Quicksand', system-ui, sans-serif", fontSize: "clamp(2rem, 4.5vw, 3.2rem)" }}>
              Enquiry Form
            </EditableText>
            <div className="relative mt-2">
              <div className="w-8 h-8 bg-[#d18a5c] rounded-sm" />
              <div className="absolute -bottom-2 -left-2 w-5 h-5 bg-[#4a2c25] rounded-sm" />
            </div>
          </div>

          <div className="space-y-5 text-[#1a1a1a]">
            <div>
              <p className="text-[#c47750] font-bold tracking-wide">Mobile</p>
              <EditableText id="contact.mobile" as="p" className="font-semibold">
                {contactInfo.mobile}
              </EditableText>
            </div>
            <div>
              <p className="text-[#c47750] font-bold tracking-wide">Email</p>
              <EditableText id="contact.email" as="p" className="font-semibold">
                {contactInfo.email}
              </EditableText>
            </div>
            <div>
              <p className="text-[#c47750] font-bold tracking-wide">Address</p>
              <EditableText id="contact.address" as="p" multiline className="font-semibold max-w-sm block whitespace-pre-line">
                {contactInfo.address}
              </EditableText>
            </div>
            <div>
              <p className="text-[#c47750] font-bold tracking-wide">Social</p>
              <div className="flex items-center gap-3 mt-2">
                <a
                  href="https://www.facebook.com/share/18xMsLrsZU/"
                  target="_blank"
                  rel="noopener"
                  referrerPolicy="no-referrer-when-downgrade"
                  aria-label="Facebook"
                  className="text-[#1877f2] hover:scale-110 transition-transform"
                >
                  <Facebook size={26} />
                </a>
              </div>
            </div>
          </div>

          {/* Live embedded Google Map */}
          <div className="mt-8 rounded-xl overflow-hidden shadow-md ring-1 ring-black/10 bg-[#e6e3d3]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3889.2138170069197!2d74.8499739!3d12.893968399999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba35b006bb9db6d%3A0xadb04f136611d6a!2sSharada%20Rama%20Rao%20Trust%20office!5e0!3m2!1sen!2sin!4v1783261954684!5m2!1sen!2sin"
              width="100%"
              height="260"
              style={{ border: 0, display: "block" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Sharada Rama Rao Trust — location"
            />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <a
              href="https://maps.app.goo.gl/YdUqr9SgNVW6b6Ts5"
              target="_blank"
              rel="noopener"
              referrerPolicy="no-referrer-when-downgrade"
              aria-label="View on Google Maps"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-white hover:bg-[#f4ecda] text-[#3a2c25] shadow-sm border border-[#e5d7bd] transition-colors"
            >
              <MapPin size={14} /> Open in Google Maps
            </a>
            <button
              type="button"
              onClick={copyAddress}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-[#c47750] hover:bg-[#b46540] text-white shadow-sm transition-colors"
            >
              {addressCopied ? <Check size={14} /> : <Copy size={14} />}
              {addressCopied ? "Copied!" : "Copy address"}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-md shadow-2xl overflow-hidden">
          <div className="bg-[#3a3f41] text-white py-6 px-8 text-center border-b-4 border-[#3a6ea5]">
            <h3 className="text-xl font-semibold tracking-wide">Contact Us</h3>
          </div>
          <form className="px-8 py-8 space-y-5" onSubmit={onSubmit}>
            <div className="grid grid-cols-[110px_1fr] items-center gap-3">
              <Label className="text-[#444]">Name <span className="text-red-500">*</span></Label>
              <Input name="name" value={form.name} onChange={onChange} className="rounded-none border-x-0 border-t-0 border-b-2 focus-visible:ring-0" />
            </div>
            <div className="grid grid-cols-[110px_1fr] items-center gap-3">
              <Label className="text-[#444]">E-mail</Label>
              <Input type="email" name="email" value={form.email} onChange={onChange} className="rounded-none border-x-0 border-t-0 border-b-2 focus-visible:ring-0" />
            </div>
            <div className="grid grid-cols-[110px_1fr] items-center gap-3">
              <Label className="text-[#444]">Contact Number <span className="text-red-500">*</span></Label>
              <Input name="phone" value={form.phone} onChange={onChange} className="rounded-none border-x-0 border-t-0 border-b-2 focus-visible:ring-0" />
            </div>
            <div className="grid grid-cols-[110px_1fr] items-start gap-3">
              <Label className="text-[#444] pt-2">Message <span className="text-red-500">*</span></Label>
              <Textarea name="message" rows={5} value={form.message} onChange={onChange} className="rounded-md focus-visible:ring-1" />
            </div>
          </form>
          <div className="relative">
            <svg viewBox="0 0 600 20" className="w-full h-5" preserveAspectRatio="none">
              <polygon points="0,20 30,0 60,20 90,0 120,20 150,0 180,20 210,0 240,20 270,0 300,20 330,0 360,20 390,0 420,20 450,0 480,20 510,0 540,20 570,0 600,20" fill="#3a3f41" />
            </svg>
          </div>
          <div className="bg-[#3a3f41] py-6 px-8 flex justify-center">
            <Button onClick={onSubmit} disabled={submitting}
              className="w-full max-w-sm rounded-full py-6 text-white shadow-lg border-2 border-[#1f3a5a] disabled:opacity-70"
              style={{ background: "linear-gradient(180deg,#5a8bc4 0%,#3a6ea5 60%,#2e5485 100%)" }}>
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
